import { uniq } from "lodash-es";
import type { Maybe } from "purify-ts";
import { type FC, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VStack } from "widgets";

import { WorkflowParameterField } from "@/modules/workflow/components/WorkflowParameterField.tsx";
import type { AllParametersTypes, WorkflowErrorParameter } from "@/modules/workflow/utils/types.ts";
import { type ActivityInstanceExtendedParameterWithActivity, buildFieldProps } from "@/redux/modules/playbook-admin.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

import type { Playbook } from "../../../graphql.js";
import {
  checkSingleProcessorActivityParameter,
  submitNfPlaybook,
  updateNfPlaybookExecutionState,
} from "../../../redux/modules/nf-playbook-execution.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type INfPlaybookProcessorParamsScreenProps = IPlaybookWizardStepProvidedProps & IPlaybookWizardStepInjectedProps;

const NfPlaybookProcessorParamsScreen: FC<INfPlaybookProcessorParamsScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const playbook = useSelector<IAppState, Playbook>((state) => state.nfPlaybookExecution.playbook);

  // read the parameters including their WorkflowParameters and activity info
  const p2Params = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity[]>(
    (state) => state.nfPlaybookExecution.p2Params,
  );

  const setValue = useCallback(
    (nodeId: string, id: string, value: Maybe<AllParametersTypes>) => {
      const newParams = p2Params.map((param) => {
        return param.activity.identifier === nodeId && param.parameterDefinition.id === id
          ? { ...param, workflowParameter: { ...param.workflowParameter, values: value } }
          : param;
      }) as ActivityInstanceExtendedParameterWithActivity[];

      dispatch(updateNfPlaybookExecutionState({ p2Params: newParams }));
    },
    [dispatch, p2Params],
  );

  const getSelectedGenomes = useCallback(() => {
    const genomeIds = p2Params
      .filter((param) => param.workflowParameter?.typeName === "GenomeParam")
      .map((param) => param.workflowParameter?.values.extractNullable())
      .filter((v): v is number => typeof v === "number");
    return uniq(genomeIds);
  }, [p2Params]);

  const [errors, setErrors] = useState<WorkflowErrorParameter[]>([]);
  const validateSingleParameter = useCallback(
    (nodeId: string, paramId: string) => {
      setErrors(dispatch(checkSingleProcessorActivityParameter(nodeId, paramId, errors)));
    },
    [dispatch, errors],
  );

  const validateAllParameters = useCallback(() => {
    let newErrors: WorkflowErrorParameter[] = [];
    for (const param of p2Params) {
      newErrors = dispatch(
        checkSingleProcessorActivityParameter(param.activity.identifier, param.parameterDefinition.id, newErrors),
      );
    }
    setErrors(newErrors);
    return newErrors;
  }, [dispatch, p2Params]);

  const [globalValidationFailed, setGlobalValidationFailed] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string>();
  const onSubmit = useCallback(() => {
    setSubmissionError(undefined);
    const errors = validateAllParameters();
    if (errors.length) {
      setGlobalValidationFailed(true);
    } else {
      dispatch(submitNfPlaybook())
        .then(() => {
          props.proceedWithNextStep();
        })
        .catch((error: Error) => {
          console.error(error.message);
          setSubmissionError(error.message);
        });
    }
  }, [dispatch, props, validateAllParameters]);

  const filteredP2Params = useMemo(() => {
    return p2Params.filter((p) => !p.activity.disabled);
  }, [p2Params]);

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
      }}
      wizardFooterData={{
        error: submissionError ?? (globalValidationFailed ? errors[0]?.message : undefined), // show the first error in the footer only upon submission failure
        nextButtonProps: {
          label: "Submit",
          onPress: onSubmit,
        },
      }}
    >
      <div style={{ maxWidth: 700, margin: "auto" }}>
        {filteredP2Params.length ? (
          <VStack spacing={20}>
            {filteredP2Params.map((parameter) => {
              const activityIdentifier = parameter.activity.identifier;
              const parameterDefinitionId = parameter.parameterDefinition.id;
              const parameterError = errors.find(
                (error) => error.nodeId === activityIdentifier && error.parameterId === parameterDefinitionId,
              );

              return (
                <WorkflowParameterField
                  key={parameter.id}
                  parameter={buildFieldProps(parameter)}
                  sampleTypeId={playbook.p2Config?.workflow?.extendedConfig.sampleType?.id}
                  validateSingleParameter={() => validateSingleParameter(activityIdentifier, parameterDefinitionId)}
                  error={parameterError}
                  value={parameter.workflowParameter?.values?.extractNullable()}
                  setValue={(values) => setValue(activityIdentifier, parameterDefinitionId, values)}
                  getSelectedGenomesIds={getSelectedGenomes}
                  isPlaybookContext={true}
                />
              );
            })}
          </VStack>
        ) : (
          <ContentPlaceholder>This Playbook does not require any input, you can proceed.</ContentPlaceholder>
        )}
      </div>
    </PlaybookWizardStep>
  );
};

export default NfPlaybookProcessorParamsScreen;
